import { createContext, FC, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGenerator, FormInput } from '@microservices/wiskey-react-components'
import { Box, Grid, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { useEditConfigMenu } from '@components/ConfigEditMenu/hooks'
import { DataSource } from '@components/DataSource'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'
import { Variables } from '@components/Variables'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import {
  ENTITY_CONFIG_TYPE,
  ENTITY_TYPES_REQUEST,
  GENERATOR_INPUT_TYPE,
  MODAL_TYPE,
  MODEL_TYPE,
} from '@constants'

import { ScriptValueDialog } from '../../components/ScriptValueDialog'

import { FieldsConfig } from './components/FieldsConfig'
import { Integration } from './components/Integration'
import { useFormCreateOrEdit } from './hooks/useFormCreateOrEdit'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const FormCreateOrEdit: FC = () => {
  const {
    configMenuOption,
    handleSelectConfigMenuOption,
    configFileText,
    handleApply,
    handleFileUpload,
    isOpenScriptDialog,
    setIsOpenScriptDialog,
    handleCancel: handleCancelEditConfig,
    handleCopyScript,
  } = useEditConfigMenu(ENTITY_CONFIG_TYPE.FORMS)

  const { data, state, handlers, mutations } = useFormCreateOrEdit()
  const { t } = useTranslation()

  const {
    formIsLoading,
    isSuccessCreate,
    formLoadingCreate,
    isSuccessUpdate,
    formLoadingUpdate,
    formCode,
    object,
    objects,
    bindingValuesFieldRender,
    bindingValuesField,
    bindingValuesFieldType,
    bindingValuesColumns,
    bindingValuesAction,
    mainTable,
    viewsData,
    viewsDataForFormIntegration,
    methods,
    isFormLoading,
    code,
    copyCode,
    isDirty,
    isEdit,
    createAllFieldsLoading,
    deleteAllFieldsLoading,
    createMandatoryFieldsLoading,
    actionTable,
    isFieldLoading,
    isActionLoading,
  } = data

  const {
    currentRow,
    currentForm,
    showDialog,
    modalType,
    modifyObjectFields,
    currentTab,
    isShowScriptValueDialog,
    objectCode,
    newModelInputsConfig,
    isShowScriptValueDialogTitle,
    contextMenuOptions,
  } = state

  const {
    handleEditField,
    handleAddField,
    handleSetModelCode,
    handleAddForm,
    handleDeleteField,
    handleCancel,
    handleSetObjectCode,
    handleCloseModal,
    handleOpenDialog,
    handleSetModalProperty,
    toggleOpenScriptValueDialog,
    handleSave,
    handleTabChange,
    handleEdit,
    handleSetShowScriptValueDialogTitle,
    handleUpdateColumnTitle,
    handleCreateAllFields,
    handleDeleteAllFields,
    handleDeleteAction,
  } = handlers

  const { createAction, editAction, updateOrderFields, updateVisibilityField, deleteField } =
    mutations

  const contextValue: PageContextType = {
    actionTable,
    objectCode: objectCode ?? '',
    formIsLoading,
    formLoadingCreate: formLoadingUpdate || formLoadingCreate,
    formCode,
    isSuccessAddForm: isSuccessCreate || isSuccessUpdate,
    currentRow,
    showDialog,
    modalType,
    modifyObjectFields,
    objectFieldsForFilter: object?.fields,
    bindingValuesFieldRender,
    bindingValuesField,
    bindingValuesFieldType,
    bindingValuesColumns,
    bindingValuesAction,
    objects,
    type: MODEL_TYPE.FORM,
    currentForm,
    mainTable,
    editField: handleEditField,
    addField: handleAddField,
    isFieldLoading,
    updateOrderFields,
    updateVisibilityField,
    onSetModelCode: handleSetModelCode,
    addForm: handleAddForm,
    deleteField: handleDeleteField,
    onCancel: handleCancel,
    onSetObjectCode: handleSetObjectCode,
    onCloseModal: handleCloseModal,
    handleShowDialog: handleOpenDialog,
    onSetModalProperty: handleSetModalProperty,
    toggleOpenScriptValueDialog,
    isShowScriptValueDialog,
    viewsByObjectCode: viewsData?.data,
    createAction,
    editAction,
    deleteAction: handleDeleteAction,
    isActionLoading,
  }

  const modelType = isEdit ? 'editModel' : 'newModel'

  const { setValue, watch } = methods

  const titleJsValue = watch('titleJsValue')
  const watchedObject = watch('object')
  const selectedObjectCode = useMemo(() => (watchedObject ? watchedObject.id : ''), [watchedObject])

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.FORMS}>
        <PageContentLayout>
          {isShowScriptValueDialogTitle && (
            <ScriptValueDialog
              windowBoundaries
              isShow={isShowScriptValueDialogTitle}
              modalType={MODAL_TYPE.EDIT}
              objectFields={object?.fields}
              value={titleJsValue || ''}
              onClose={() => handleSetShowScriptValueDialogTitle(false)}
              onSave={value => setValue('titleJsValue', value, { shouldDirty: true })}
            />
          )}
          <EditConfigScriptDialog />
          <Box sx={{ width: '100%' }}>
            <form>
              <FormProvider {...methods}>
                <Grid container px={1} py={0.5}>
                  <Grid container justifyContent={'space-between'}>
                    <Typography py={0.5} variant='h5'>
                      {t(`viewsCreate.${modelType}.${MODEL_TYPE.FORM}`)}
                    </Typography>
                    {currentForm?.id && <EditConfigMenu configId={currentForm?.id} />}
                  </Grid>
                  <Grid container display='flex' justifyContent='space-between'>
                    <Grid item md={5.9}>
                      <Typography py={0.5} variant='h5'>
                        {t('viewsCreate.general')}
                      </Typography>
                      <FormGenerator inputs={newModelInputsConfig} mainGridSpacing={0} />
                    </Grid>
                    <Grid item md={5.9}>
                      <DataSource
                        isDisabledObject={!!code || !!copyCode}
                        isLoading={isFormLoading}
                        objects={objects}
                      />
                      <Integration loading={isFormLoading} views={viewsData?.data} />
                      <FormInput
                        autocompleteOptions={contextMenuOptions}
                        inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                        label={t('formCreate.contextMenu.label')}
                        name='contextMenu'
                        placeholder={t('formCreate.contextMenu.placeholder')}
                      />
                    </Grid>
                  </Grid>
                  <Grid item md>
                    <CommandPickerController
                      isAnotherObjectWithRequestData
                      commandName='name'
                      objectCode={selectedObjectCode}
                    />
                  </Grid>
                </Grid>
              </FormProvider>
            </form>
            <Grid container justifyContent={'flex-end'}>
              <GeneralButtons
                cancelText={t('viewOrFormCreate.cancelBtnText')}
                isLoading={formLoadingUpdate || formLoadingCreate || formIsLoading}
                saveDisabled={!isDirty || formLoadingUpdate || formLoadingCreate || formIsLoading}
                saveText={t('viewOrFormCreate.saveBtnText')}
                onCancel={handleCancel}
                onSave={handleSave}
              />
            </Grid>
            <Box sx={{ pl: 1, pt: 0 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                  <Tab label={`${t(`viewsCreate.forms.name`)}`} tabIndex={0} />
                  <Tab label={`${t(`viewsCreate.forms.actions`)}`} tabIndex={1} />
                  <Tab label={`${t(`viewsCreate.forms.variables`)}`} tabIndex={2} />
                  <Tab label={`${t(`viewsCreate.forms.config`)}`} tabIndex={3} />
                </Tabs>
              </Box>
              <TabPanel index={0} value={currentTab}>
                <SwitchTable
                  disablePagination
                  isCrud
                  btnDisabled={!isEdit && (!isSuccessCreate || isSuccessUpdate)}
                  btnText={t(`viewsCreate.forms.btnText`)}
                  columns={mainTable.columns}
                  createAllBtnText={t('configEntity.allForms.createAll')}
                  createAllRequiredBtnText={t('configEntity.allForms.createAllMandatory')}
                  deleteAllBtnText={t('configEntity.deleteAll')}
                  isCreateAllLoading={createAllFieldsLoading}
                  isCreateMandatoryLoading={createMandatoryFieldsLoading}
                  isDeleteAllLoading={deleteAllFieldsLoading}
                  loading={formIsLoading}
                  rows={mainTable.rows}
                  showActionsColumn={!copyCode}
                  showDialog={showDialog}
                  type={MODEL_TYPE.FORM}
                  updateRow={handleUpdateColumnTitle}
                  containerSx={{
                    '.MuiTableBody-root > div': {
                      height: '400px !important',
                    },
                  }}
                  onCreateAllEntityFields={handleCreateAllFields}
                  onDelete={deleteField}
                  onDeleteAllEntityFields={handleDeleteAllFields}
                  onEdit={params => handleEdit(params.row.original.id)}
                  onShowDialog={handleOpenDialog}
                />
              </TabPanel>
              <TabPanel index={1} value={currentTab}>
                <SwitchTable
                  disablePagination
                  isCrud
                  btnDisabled={!isEdit}
                  btnText={t(`configEntity.form.actions.addButton`)}
                  columns={actionTable?.columns}
                  loading={formIsLoading}
                  rows={actionTable.rows}
                  showActionsColumn={!copyCode}
                  showDialog={showDialog}
                  type={MODEL_TYPE.FORM_ACTION}
                  containerSx={{
                    '.MuiTableBody-root > div': {
                      height: '400px !important',
                    },
                  }}
                  onDelete={handleDeleteAction}
                  onEdit={params => handleEdit(params.row.original.id, true)}
                  onShowDialog={handleOpenDialog}
                />
              </TabPanel>
              <TabPanel contentSx={{ p: 0 }} index={2} value={currentTab}>
                <Variables
                  entityId={currentForm?.id}
                  entityType={ENTITY_TYPES_REQUEST.FORM}
                  isEditEntity={isEdit}
                />
              </TabPanel>
              <TabPanel contentSx={{ p: 0 }} index={3} value={currentTab}>
                <FieldsConfig
                  actions={actionTable.rows}
                  formCode={code}
                  formId={currentForm?.id}
                  objectCode={objectCode}
                />
              </TabPanel>
            </Box>
          </Box>
        </PageContentLayout>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
